import React, { useState, useEffect, useRef } from 'react';
import {
    FaUser, FaEye, FaMapMarkerAlt, FaBriefcase,
    FaCheckCircle, FaSpinner, FaThLarge, FaList, FaUsers, 
    FaPhone, FaLock, FaHeart, FaStar, FaTimes
} from 'react-icons/fa';
import {
    getBiodataMatches,
    sendInterest,
    getInterests,
    getUserActivePlans,
    getBoostedProfiles,
} from '../../Api/CoreApi';
import { BASE_API } from "../../Api/Api";
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { handleShortlistUser, fetchShortlistedUsers, addShortlistListener } from '../../utils/shortlistUtils';
import './AllUsers.css'; // ✅ Imported External CSS

// ✅ Food lifestyle backend → display label map
const FOOD_BACKEND_TO_DISPLAY = {
    'vegetarian': 'Vegetarian',
    'non_vegetarian': 'Non-Vegetarian',
    'eggetarian': 'Eggetarian',
    'vegan': 'Vegan',
    'jain': 'Jain',
};

const FOOD_DISPLAY_TO_BACKEND = {
    'Vegetarian': 'vegetarian',
    'Non-Vegetarian': 'non_vegetarian',
    'Eggetarian': 'eggetarian',
    'Vegan': 'vegan',
    'Jain': 'jain',
};

const getFoodDisplayLabel = (user) => {
    if (user.food_lifestyle_display) return user.food_lifestyle_display;
    if (user.food_lifestyle) {
        const lower = user.food_lifestyle.trim().toLowerCase();
        return FOOD_BACKEND_TO_DISPLAY[lower] || user.food_lifestyle;
    }
    if (user.diet) {
        const lower = user.diet.trim().toLowerCase();
        return FOOD_BACKEND_TO_DISPLAY[lower] || user.diet;
    }
    return '';
};

const AllUsers = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [shortlistedIds, setShortlistedIds] = useState([]);
    const [interestSentIds, setInterestSentIds] = useState([]);
    const [actionLoading, setActionLoading] = useState({});
    const [currentUserGender, setCurrentUserGender] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [genderLoaded, setGenderLoaded] = useState(false);
    const [activePlans, setActivePlans] = useState([]);
    const [viewedContacts, setViewedContacts] = useState([]);
    const [boostedIds, setBoostedIds] = useState([]);
    const [currentUserCaste, setCurrentUserCaste] = useState('');
    const [showLockModal, setShowLockModal] = useState(false);

    const navigate = useNavigate();

    const [advancedFilters, setAdvancedFilters] = useState({
        states: [],
        districts: [],
        motherTongues: [],
        foodStyles: [],
        jobs: [],
        educations: [],
        ageMin: '',
        ageMax: '',
        salaryMin: '',
        salaryMax: '',
        heightMin: '',
        heightMax: '',
        maritalStatus: 'all',
        smoking: 'all',
        drinking: 'all',
        showOnlyVerified: false,
        showOnlyOnline: false,
        showOnlyWithImages: false,
    });

    // ============================================
// 🔄 SYNC SEARCH & FILTERS FROM HEADER
// ============================================
useEffect(() => {
    const emptyFiltersTemplate = {
        states: [], districts: [], motherTongues: [], foodStyles: [], jobs: [], educations: [],
        ageMin: '', ageMax: '', salaryMin: '', salaryMax: '', heightMin: '', heightMax: '',
        maritalStatus: 'all', smoking: 'all', drinking: 'all',
        showOnlyVerified: false, showOnlyOnline: false, showOnlyWithImages: false
    };

    const handleSyncSearch = (data) => {
        if (!data) return;

        // Set search term (empty string is valid - means cleared)
        if (data.searchTerm !== undefined) {
            setSearchTerm(data.searchTerm || '');
        }

        // Set advanced filters
        if (data.advancedFilters) {
            // Check if the incoming filters are essentially "empty/cleared"
            const incomingFilters = data.advancedFilters;
            const isClearing = 
                (!incomingFilters.states || incomingFilters.states.length === 0) &&
                (!incomingFilters.districts || incomingFilters.districts.length === 0) &&
                (!incomingFilters.motherTongues || incomingFilters.motherTongues.length === 0) &&
                (!incomingFilters.foodStyles || incomingFilters.foodStyles.length === 0) &&
                (!incomingFilters.jobs || incomingFilters.jobs.length === 0) &&
                (!incomingFilters.educations || incomingFilters.educations.length === 0) &&
                !incomingFilters.ageMin && !incomingFilters.ageMax &&
                !incomingFilters.heightMin && !incomingFilters.heightMax &&
                (incomingFilters.maritalStatus === 'all' || !incomingFilters.maritalStatus) &&
                !incomingFilters.showOnlyVerified &&
                !incomingFilters.showOnlyOnline &&
                !incomingFilters.showOnlyWithImages;

            if (isClearing) {
                // ✅ Reset to full empty state - this will show all profiles
                setAdvancedFilters(emptyFiltersTemplate);
            } else {
                // Merge incoming filters with template to ensure all keys exist
                setAdvancedFilters({
                    ...emptyFiltersTemplate,
                    ...incomingFilters
                });
            }
        }
    };

    // 1. Initial Load: Check sessionStorage
    const storedParams = sessionStorage.getItem('allUsersSearchParams');
    if (storedParams) {
        try {
            const parsed = JSON.parse(storedParams);
            handleSyncSearch(parsed);
            sessionStorage.removeItem('allUsersSearchParams');
        } catch (e) {
            console.error("Error parsing stored search parameters:", e);
        }
    }

    // 2. Real-Time Listen for Header events
    const onSearchApplied = (e) => {
        handleSyncSearch(e.detail);
    };

    window.addEventListener('allUsersSearchApplied', onSearchApplied);
    return () => {
        window.removeEventListener('allUsersSearchApplied', onSearchApplied);
    };
}, []);

    // ============================================
    // PLAN CHECK HELPERS
    // ============================================
    const hasActivePlan = () => isLoggedIn && activePlans.length > 0;
    const isProfileUnlocked = (userId) => viewedContacts.includes(String(userId));

    const canSendInterest = (userId) => {
        if (interestSentIds.includes(String(userId))) return true;
        if (isProfileUnlocked(String(userId))) return true;
        if (hasActivePlan()) return true;
        return false;
    };

    // ============================================
    // DISPLAY HELPERS
    // ============================================
    const getDisplayId = (user) => {
        if (!user) return 'N/A';
        if (user.profile_id) return `${user.profile_id}`;
        if (user.User_id || user.user_id || user.id) return user.User_id || user.user_id || user.id;
        return 'N/A';
    };

    const getOppositeGender = (gender) => {
        if (!gender) return '';
        if (gender === 'Male') return 'Female';
        if (gender === 'Female') return 'Male';
        return '';
    };

    const getGenderFilterInfo = () => {
        if (!isLoggedIn || !currentUserGender) return null;
        const opposite = getOppositeGender(currentUserGender);
        return { showing: opposite, icon: opposite === 'Female' ? '👩' : '👨' };
    };

    const getDisplayName = (user) =>
        user.name || user.firstname || user.fullname || user.first_name || user.username || 'Unknown';

    const getDisplayImage = (user) => {
        if (user.image && typeof user.image === 'string') return user.image;
        if (user.pic && typeof user.pic === 'string') return user.pic;
        return `https://ui-avatars.com/api/?name=${getDisplayName(user)}&background=ff0055&color=fff&size=128`;
    };

    const getDisplayLocation = (user) => user.location || user.city || user.district || user.state || 'N/A';

    // ============================================
    // API CALLS
    // ============================================
    const fetchUserPlans = async () => {
        try {
            const data = await getUserActivePlans();
            setActivePlans(data?.plans || []);
            setViewedContacts(data?.viewed_contacts || []);
        } catch (err) {
            setActivePlans([]);
            setViewedContacts([]);
        }
    };

    const fetchBoostedProfilesData = async () => {
        try {
            const data = await getBoostedProfiles();
            setBoostedIds(data.boosted_user_ids || []);
        } catch (err) { }
    };

    const getCurrentUserGender = async () => {
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('access_token');
        if (!userId || !token) {
            setIsLoggedIn(false);
            setGenderLoaded(true);
            return null;
        }
        setIsLoggedIn(true);
        try {
            const savedGender = localStorage.getItem('user_gender');
            const savedCaste = localStorage.getItem('user_caste');
            if (savedGender && savedCaste) {
                setCurrentUserGender(savedGender);
                setCurrentUserCaste(savedCaste);
                setGenderLoaded(true);
                return savedGender;
            }
            const response = await fetch(`${BASE_API}/UserData/`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const dataArray = await response.json();
                const currentUser = Array.isArray(dataArray) ? dataArray.find(u => String(u?.user || u?.user_id || u?.id || u?.User_id) === String(userId)) : null;
                if (currentUser) {
                    if (currentUser?.gender) { setCurrentUserGender(currentUser.gender); localStorage.setItem('user_gender', currentUser.gender); }
                    if (currentUser?.caste) { setCurrentUserCaste(currentUser.caste); localStorage.setItem('user_caste', currentUser.caste); }
                    setGenderLoaded(true);
                    return currentUser?.gender;
                }
            }
            setGenderLoaded(true);
            return null;
        } catch (error) {
            setGenderLoaded(true);
            return null;
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await getBiodataMatches();
            let allMatches = [];
            if (data.topMatch && data.topMatch.id) allMatches.push(data.topMatch);
            if (data.moreMatches && Array.isArray(data.moreMatches)) allMatches = [...allMatches, ...data.moreMatches];
            if (allMatches.length === 0 && data.profiles) allMatches = data.profiles || [];

            // Remove duplicates & empty profiles
            const seenIds = new Set();
            allMatches = allMatches.filter(m => {
                const id = m.id || m.bid || m.User_id || m.user_id;
                if (!id || seenIds.has(String(id))) return false;
                seenIds.add(String(id));
                return m.firstname || m.username || m.name || m.fullname;
            });

            setUsers(allMatches);
            setFilteredUsers(allMatches);

            // Fetch relations
            try { setShortlistedIds((await fetchShortlistedUsers(true)) || []); } catch (e) { setShortlistedIds([]); }
            try {
                const interests = await getInterests();
                const backendIds = interests.map(item => String(item.to_user || '')).filter(Boolean);
                const localIds = JSON.parse(localStorage.getItem('interest_sent_ids') || '[]');
                const mergedIds = [...new Set([...backendIds, ...localIds])];
                setInterestSentIds(mergedIds);
                localStorage.setItem('interest_sent_ids', JSON.stringify(mergedIds));
            } catch (e) { }

        } catch (err) {
            setError('Failed to load profiles');
        } finally {
            setLoading(false);
        }
    };

    const handleClearAllFilters = () => {
        const emptyFilters = {
            states: [], districts: [], motherTongues: [], foodStyles: [], jobs: [], educations: [],
            ageMin: '', ageMax: '', salaryMin: '', salaryMax: '', heightMin: '', heightMax: '',
            maritalStatus: 'all', smoking: 'all', drinking: 'all', showOnlyVerified: false,
            showOnlyOnline: false, showOnlyWithImages: false
        };
        setAdvancedFilters(emptyFilters);
        setSearchTerm('');
        sessionStorage.removeItem('allUsersSearchParams');
        // Notify Header to reset its internal states
        window.dispatchEvent(new CustomEvent('allUsersSearchApplied', {
            detail: { searchTerm: '', advancedFilters: emptyFilters }
        }));
    };

    // ============================================
    // FILTER EFFECT
    // ============================================
    useEffect(() => {
        let result = [...users];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(u => Object.values(u).some(val => String(val).toLowerCase().includes(term)));
        }
        if (advancedFilters.ageMin) result = result.filter(u => parseInt(u.age) >= parseInt(advancedFilters.ageMin));
        if (advancedFilters.ageMax) result = result.filter(u => parseInt(u.age) <= parseInt(advancedFilters.ageMax));
        if (advancedFilters.salaryMin) result = result.filter(u => parseInt(u.annual_income || u.salary) >= parseInt(advancedFilters.salaryMin));
        if (advancedFilters.salaryMax) result = result.filter(u => parseInt(u.annual_income || u.salary) <= parseInt(advancedFilters.salaryMax));

        const parseHeight = (h) => {
            const str = String(h || '').toLowerCase().trim();
            if (str.includes('cm')) return parseFloat(str);
            const ft = str.match(/(\d+)\s*'\s*(\d+)?/);
            if (ft) return Math.round((parseInt(ft[1] || 0) * 12 + parseInt(ft[2] || 0)) * 2.54);
            return parseFloat(str.match(/\d+(\.\d+)?/)?.[0] || null);
        };
        if (advancedFilters.heightMin) result = result.filter(u => parseHeight(u.height) >= parseFloat(advancedFilters.heightMin));
        if (advancedFilters.heightMax) result = result.filter(u => parseHeight(u.height) <= parseFloat(advancedFilters.heightMax));

        if (advancedFilters.states?.length > 0) result = result.filter(u => advancedFilters.states.some(s => u.state?.toLowerCase() === s.toLowerCase()));
        if (advancedFilters.districts?.length > 0) result = result.filter(u => advancedFilters.districts.some(d => u.district?.toLowerCase() === d.toLowerCase()));
        if (advancedFilters.jobs?.length > 0) result = result.filter(u => advancedFilters.jobs.some(j => (u.job_title || u.occupation || '').toLowerCase().includes(j.toLowerCase())));
        if (advancedFilters.educations?.length > 0) result = result.filter(u => advancedFilters.educations.some(e => (u.course || u.education || '').toLowerCase().includes(e.toLowerCase())));
        if (advancedFilters.motherTongues?.length > 0) result = result.filter(u => advancedFilters.motherTongues.some(t => u.mother_tongue?.toLowerCase() === t.toLowerCase()));

        if (advancedFilters.foodStyles?.length > 0) {
            result = result.filter(u => {
                const storedVal = (u.food_lifestyle || '').trim().toLowerCase();
                const displayVal = (u.food_lifestyle_display || '').trim().toLowerCase();
                const dietVal = (u.diet || '').trim().toLowerCase();
                return advancedFilters.foodStyles.some(selected => {
                    const selLow = selected.toLowerCase();
                    const backKey = FOOD_DISPLAY_TO_BACKEND[selected] || selLow;
                    return displayVal === selLow || storedVal === backKey || dietVal === backKey || dietVal === selLow;
                });
            });
        }

        if (advancedFilters.maritalStatus !== 'all') result = result.filter(u => (u.marrige_status || u.marital_status)?.toLowerCase() === advancedFilters.maritalStatus.toLowerCase());
        if (advancedFilters.smoking !== 'all') result = result.filter(u => u.smoking?.toLowerCase() === advancedFilters.smoking.toLowerCase());
        if (advancedFilters.drinking !== 'all') result = result.filter(u => u.drinking?.toLowerCase() === advancedFilters.drinking.toLowerCase());
        if (advancedFilters.showOnlyVerified) result = result.filter(u => u.verified);
        if (advancedFilters.showOnlyOnline) result = result.filter(u => u.is_online);
        if (advancedFilters.showOnlyWithImages) result = result.filter(u => !(!u.pic || u.pic.includes('profilepic.jpg') || u.pic.includes('default')));

        result.sort((a, b) => {
            const aId = String(a.User_id || a.user_id || a.user || a.id || a.bid || '');
            const bId = String(b.User_id || b.user_id || b.user || b.id || b.bid || '');
            return (boostedIds.includes(bId) ? 1 : 0) - (boostedIds.includes(aId) ? 1 : 0);
        });

        setFilteredUsers(result);
    }, [searchTerm, advancedFilters, users, boostedIds]);

    // INITIALIZE
    useEffect(() => {
        const initialize = async () => {
            await getCurrentUserGender();
            await fetchUserPlans();
            await fetchBoostedProfilesData();
            await fetchUsers();
        };
        initialize();
        return addShortlistListener((newIds) => setShortlistedIds(newIds));
    }, []);

    const handleShortlist = async (userId) => {
        if (!isLoggedIn) return navigate('/User_Login');
        setActionLoading(prev => ({ ...prev, [userId]: 'shortlist' }));
        await handleShortlistUser(userId, setShortlistedIds);
        setActionLoading(prev => ({ ...prev, [userId]: null }));
    };

    const handleInterest = async (userId) => {
        if (!isLoggedIn) return navigate('/User_Login');
        if (interestSentIds.includes(String(userId))) return;
        if (!canSendInterest(userId)) return setShowLockModal(true);
        setActionLoading(prev => ({ ...prev, [userId]: 'interest' }));
        try {
            const response = await sendInterest(userId);
            if (response && response.id) {
                setInterestSentIds(prev => [...prev, String(userId)]);
                message.success("Interest sent!");
            }
        } catch (err) { message.info("Interest already sent or failed!"); }
        setActionLoading(prev => ({ ...prev, [userId]: null }));
    };

    if (loading || !genderLoaded) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16, background: '#fff', padding: '20px' }}>
                <FaSpinner size={40} style={{ color: '#ff0055', animation: 'auSpin 1s linear infinite' }} />
                <p style={{ color: '#888', fontSize: 13, textAlign: 'center' }}>💖 Loading matched profiles...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', background: '#fff', padding: '16px' }}>
                <div style={{ background: '#fff', border: '2px solid #ffe4ec', borderRadius: 20, padding: '30px 20px', width: '100%', maxWidth: 400, textAlign: 'center' }}>
                    <h2 style={{ color: '#ff0055', marginBottom: 12, fontSize: 20 }}>⚠️ Error</h2>
                    <p style={{ color: '#888', fontSize: 13 }}>{error}</p>
                    <button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '11px 30px', background: 'linear-gradient(135deg,#ff0055,#ff4d88)', color: '#fff', border: 'none', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Try Again</button>
                </div>
            </div>
        );
    }

    const LockModal = () => (
        <div className="au-lock-overlay" onClick={() => setShowLockModal(false)}>
            <div className="au-lock-modal" onClick={(e) => e.stopPropagation()}>
                <button className="au-lock-close" onClick={() => setShowLockModal(false)}><FaTimes /></button>
                <div className="au-lock-icon">🔒</div>
                <h3 className="au-lock-title">Plan Required!</h3>
                <p className="au-lock-desc">You need an active plan to send interest.</p>
                <div className="au-lock-features">
                    <div className="au-lock-feature"><FaHeart style={{ color: '#ff0055' }} /><span>Unlimited interests</span></div>
                    <div className="au-lock-feature"><FaPhone style={{ color: '#ff0055' }} /><span>View contacts</span></div>
                </div>
                <button className="au-lock-btn-primary" onClick={() => navigate('/recharge')}>🚀 Buy Plan</button>
                <button className="au-lock-btn-secondary" onClick={() => setShowLockModal(false)}>Maybe Later</button>
            </div>
        </div>
    );

    return (
        <>
            {showLockModal && <LockModal />}
            <div className="au-page">
                <div className="au-wrap">
                    <div className="au-header">
                        <div className="au-title-section">
                            <h1 className="au-title"><FaUsers size={20} /> All Profiles</h1>
                            <p className="au-subtitle">
                                <strong>{filteredUsers.length}</strong> matched profiles found 💖
                                {getGenderFilterInfo() && <span style={{ marginLeft: 6, color: '#888' }}>· {getGenderFilterInfo().icon} Showing {getGenderFilterInfo().showing} profiles</span>}
                            </p>
                        </div>

                        {/* Layout Toggle Action */}
                        <div className="au-view-toggle">
                            <button className={`au-view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}><FaThLarge size={12} /></button>
                            <button className={`au-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}><FaList size={12} /></button>
                        </div>
                    </div>

                    {activePlans.length === 0 && (
                        <div className="au-plan-banner" style={{ background: 'linear-gradient(135deg,#fff0f3,#ffe4ec)', border: '2px solid #ffd3df', marginTop: 15 }}>
                            <div style={{ fontSize: 28 }}>🔒</div>
                            <div className="au-plan-banner-text">
                                <p style={{ fontSize: 12, fontWeight: 800, color: '#c2185b', margin: 0 }}>Purchase a plan to send interest!</p>
                            </div>
                            <button onClick={() => navigate('/recharge')} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#ff0055,#ff4d88)', color: '#fff', border: 'none', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Buy Plan</button>
                        </div>
                    )}

                    {filteredUsers.length > 0 ? (
                        <div className={`au-grid ${viewMode === 'grid' ? 'grid-mode' : 'list-mode'}`} style={{ marginTop: 20 }}>
                            {filteredUsers.map((user) => {
                                const userId = user.User_id || user.user_id || user.user || user.id || user.bid;
                                const isShortlisted = shortlistedIds.includes(String(userId));
                                const isInterest = interestSentIds.includes(String(userId));
                                const isLoad = actionLoading[userId];
                                const isBoosted = boostedIds.includes(String(userId));

                                return (
                                    <div key={userId} className={`au-card ${isBoosted ? 'boosted' : ''} ${viewMode === 'list' ? 'list-card' : ''}`}>
                                        {isBoosted && <div className="au-boost-tag">🚀 BOOSTED</div>}
                                        <div className="au-card-img-section">
                                            <div className="au-avatar-wrap">
                                                <img src={getDisplayImage(user)} alt={getDisplayName(user)} className="au-avatar" onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${getDisplayName(user)}&background=ff0055&color=fff&size=128`; }} />
                                                {user.is_online && <span className="au-online-dot" />}
                                            </div>
                                        </div>
                                        <div className="au-card-body">
                                            <div className="au-card-top-row">
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <h3 className="au-name">{getDisplayName(user)}</h3>
                                                    <p className="au-id">ID: {getDisplayId(user)}</p>
                                                </div>
                                            </div>
                                            <div className="au-info-chips">
                                                {(user.age || user.height) && <span className="au-info-chip"><FaUser size={9} /> {user.age} Yrs {user.height ? `, ${user.height}` : ''}</span>}
                                                {getDisplayLocation(user) !== 'N/A' && <span className="au-info-chip"><FaMapMarkerAlt size={9} /> {getDisplayLocation(user)}</span>}
                                            </div>
                                            <div className="au-actions-row">
                                                <button className="au-action-btn au-btn-view" onClick={() => navigate(`/user-profile/${userId}`)}><FaEye size={10} /> View</button>
                                                <button className={`au-action-btn au-btn-interest ${isInterest ? 'sent' : !canSendInterest(userId) ? 'locked' : ''}`} onClick={() => handleInterest(userId)} disabled={isLoad === 'interest' || isInterest}>
                                                    {isLoad === 'interest' ? <FaSpinner size={10} style={{ animation: 'auSpin 1s linear infinite' }} /> : isInterest ? <>✅ Sent</> : !canSendInterest(userId) ? <><FaLock size={10} /> Locked</> : <><FaHeart size={10} /> Interest</>}
                                                </button>
                                                <button className={`au-action-btn au-btn-shortlist ${isShortlisted ? 'done' : ''}`} onClick={() => handleShortlist(userId)} disabled={isLoad === 'shortlist' || isShortlisted}>
                                                    {isLoad === 'shortlist' ? <FaSpinner size={10} style={{ animation: 'auSpin 1s linear infinite' }} /> : isShortlisted ? <>⭐ Saved</> : <><FaStar size={10} /> Save</>}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="au-empty" style={{ marginTop: 40 }}>
                            <div className="au-empty-icon"><FaUser /></div>
                            <h3 className="au-empty-title">No Profiles Found</h3>
                            <button className="au-empty-btn" onClick={handleClearAllFilters}>Reset Filters</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AllUsers;